import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fieldCategoriesApi,
  type CreateFieldCategoryPayload,
  type FieldCategoryListParams,
  type UpdateFieldCategoryPayload,
} from '@/api/fieldCategoriesApi'

export type { FieldCategoryListParams }

/** Prefix key used for invalidating all Field Category queries. */
const FIELD_CATEGORIES_KEY = ['fieldCategories'] as const
/** Prefix key for the field library — cross-invalidated when categories change so
 *  per-row category tags stay fresh (e.g. after a rename or delete). */
const FIELD_LIBRARY_KEY = ['fieldLibrary'] as const

/** List query + create/update/delete mutations for Field Categories.
 *  Pass `enabled=false` to suppress the list query for users without read permission. */
export function useFieldCategories(params: FieldCategoryListParams = {}, enabled = true) {
  const qc = useQueryClient()

  const invalidateCategories = () => {
    void qc.invalidateQueries({ queryKey: FIELD_CATEGORIES_KEY })
  }

  /** Invalidate both categories and the field library so per-row tags refresh. */
  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: FIELD_CATEGORIES_KEY })
    void qc.invalidateQueries({ queryKey: FIELD_LIBRARY_KEY })
  }

  const list = useQuery({
    queryKey: ['fieldCategories', 'list', params],
    queryFn: () => fieldCategoriesApi.list(params),
    placeholderData: keepPreviousData,
    enabled,
  })

  const create = useMutation({
    mutationFn: (payload: CreateFieldCategoryPayload) => fieldCategoriesApi.create(payload),
    onSuccess: invalidateCategories,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: UpdateFieldCategoryPayload }) =>
      fieldCategoriesApi.update(input.id, input.payload),
    // Rename changes the display name shown in field-library rows — invalidate both.
    onSuccess: invalidateAll,
  })

  const remove = useMutation({
    mutationFn: (id: string) => fieldCategoriesApi.remove(id),
    // Delete removes the association — field rows must re-fetch to drop the stale id.
    onSuccess: invalidateAll,
  })

  return { list, create, update, remove }
}

/** Read-only list query — use in contexts that do not need mutations (pickers/filter/tags).
 *  Pass `enabled=false` to suppress the query for users without read permission. */
export function useFieldCategoriesList(params: FieldCategoryListParams = {}, enabled = true) {
  return useQuery({
    queryKey: ['fieldCategories', 'list', params],
    queryFn: () => fieldCategoriesApi.list(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}
